import React, { useEffect, useMemo, useSyncExternalStore } from 'react';
import { Select, Stack, Text } from '@mantine/core';
import {
  getWebLinksSnapshot,
  loadWebLinks,
  subscribeWebLinks,
} from '../../../utils/webLinkRegistry';
import { buildWebLinkOptions, findWebLink } from '../../../utils/webLinks';
import {
  getVariableCatalogSnapshot,
  getVariableCatalogSource,
  loadVariableCatalog,
  subscribeVariableCatalog,
} from '../../../utils/variableCatalogRegistry';
import { variableSelectOptions } from '../../../utils/variableCatalog';
import { looksLikeUrl } from '../../../../shared/boxes/button/buttonLink';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import {
  LostTargetAlert, isLost, withChosenOption, withLostOption,
} from '../../../puck/fields/lostTarget';
import { renderWebLinkOption } from '../../../puck/fields/selectOptionHint';
import { puckDebouncedTextField } from '../../../puck/fields/puckDebouncedTextField';
import { EMBED_SERVICE_LABELS, extractIframeSrc, resolveEmbed } from './embedCatalog';
import {
  EMBED_SOURCES, EMBED_SOURCE_LABELS, embedSourceKind, embedSourceReset,
} from './embedSource';

/* Le champ d'adresse libre reste celui d'avant, débouncé : la frappe vit en
   local, le store n'est écrit qu'à la pause. Google Maps ne propose de copier
   qu'un extrait HTML, dont `extractIframeSrc` ne garde que l'adresse. */
const urlField = puckDebouncedTextField('Lien à intégrer', {
  placeholder: `${EMBED_SERVICE_LABELS.join(', ')}…`,
  transform: extractIframeSrc,
});

/** Une valeur de variable réellement intégrable dans un iframe. */
const embeddableVariable = (variable) => looksLikeUrl(variable.value)
  && resolveEmbed(variable.value).ok;

/**
 * Adresse du bloc « Contenu externe » : un lien de la banque, une variable du
 * CRM, ou une adresse saisie à la main — même grammaire que « Le bouton mène
 * vers » (`ButtonLinkField`), un seul champ visible à la fois.
 *
 * Les sélecteurs ne proposent **que l'intégrable** : un lien LinkedIn n'y
 * apparaît pas, il ne peut pas vivre dans un iframe. Le filtre passe par
 * `resolveEmbed`, donc par le catalogue fermé lui-même : il n'existe pas de
 * seconde liste de domaines à tenir à jour.
 *
 * Le champ écrit **plusieurs** props (`url`, `linkId`, le triplet variable),
 * ce qu'un `onChange` de champ Puck ne permet pas — d'où `updateItemProps`, le
 * mécanisme déjà employé par `ProposalSectionColumnsField`.
 */
export default function EmbedLinkField({ value, onChange }) {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const source = embedSourceKind(itemProps);

  /* Banque et catalogue de variables ne se lisent qu'une fois leur source
     choisie : ouvrir la sidebar d'un embed ne doit déclencher aucun appel
     (même règle que le menu `@` et que la banque d'images). */
  const bank = useSyncExternalStore(subscribeWebLinks, getWebLinksSnapshot);
  useEffect(() => { if (source === 'bank') loadWebLinks(); }, [source]);

  const {
    catalog, status: catalogStatus,
  } = useSyncExternalStore(subscribeVariableCatalog, getVariableCatalogSnapshot);
  useEffect(() => { if (source === 'variable') loadVariableCatalog(); }, [source]);

  const bankOptions = useMemo(
    () => (source === 'bank'
      ? buildWebLinkOptions(bank.groups, (link) => resolveEmbed(link.url).ok)
      : []),
    [source, bank.groups],
  );

  /* Un modèle de site sert ses variables sans valeur (`variable_names_only`) :
     le filtre « ça s'intègre » n'y trouverait rien. On propose la liste
     entière, et la valeur est écrite à la génération du site du document. */
  const isTemplate = getVariableCatalogSource()?.type === 'web_template';
  const variableOptions = useMemo(
    () => {
      if (source !== 'variable') return [];
      return variableSelectOptions(catalog, isTemplate ? undefined : embeddableVariable);
    },
    [source, isTemplate, catalog],
  );

  const linkId = itemProps.linkId ? String(itemProps.linkId) : null;
  const variableKey = itemProps.variableKey || null;

  /* Une variable choisie mais écartée du filtre — pas encore renseignée, ou sa
     valeur ne s'intègre pas — n'est PAS une variable perdue : elle vit très
     bien dans le CRM. On la réaffiche par son nom, et l'alerte est réservée au
     cas où le catalogue ne la connaît plus. */
  const chosenVariable = variableKey ? catalog.byKey[variableKey] : null;
  const variableGone = catalogStatus === 'ready' && !!variableKey && !chosenVariable;

  return (
    <Stack gap={6}>
      <Select
        size="xs"
        label="Le contenu vient de"
        wrapperProps={{ 'data-inspector-layout': 'stacked' }}
        data={EMBED_SOURCES.map((kind) => ({ value: kind, label: EMBED_SOURCE_LABELS[kind] }))}
        value={source}
        onChange={(next) => next && next !== source && updateItemProps(embedSourceReset(next))}
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
      />

      {source === 'bank' && (
        <>
          <Select
            size="xs"
            label="Lien"
            wrapperProps={{ 'data-inspector-layout': 'stacked' }}
            placeholder={bank.status === 'error' ? 'Banque indisponible' : 'Choisir un lien'}
            data={withLostOption(bankOptions, linkId, bank.status === 'ready', 'Lien supprimé')}
            value={linkId}
            searchable
            clearable
            renderOption={renderWebLinkOption}
            disabled={bank.status === 'loading' || bank.status === 'error'}
            nothingFoundMessage="Aucun lien intégrable"
            /* On copie l'adresse : le site publié n'interroge jamais la banque,
               et supprimer un lien ne casse aucune page. */
            onChange={(id) => {
              const link = findWebLink(bank.links, id);
              updateItemProps(link
                ? { url: link.url, linkId: String(link.id) }
                : { url: '', linkId: '' });
            }}
            comboboxProps={{ withinPortal: false }}
          />
          {bank.status === 'ready' && !bankOptions.length && bank.links.length > 0 && (
            <Text size="xs" c="dimmed">
              Aucun lien de la banque ne peut être intégré dans une page.
            </Text>
          )}
          {bank.status === 'error' && (
            <Text size="xs" c="dimmed">
              La banque de liens n’a pas pu être lue. Saisissez une adresse web
              en attendant.
            </Text>
          )}
          <LostTargetAlert
            shown={bank.status === 'ready' && isLost(bankOptions, linkId)}
            what="Ce lien n’est plus dans la banque"
            consequence={'. Le bloc affiche toujours l’adresse enregistrée '
              + '; elle ne se mettra plus à jour.'}
          />
        </>
      )}

      {source === 'variable' && (
        <>
          <Select
            size="xs"
            label="Variable"
            wrapperProps={{ 'data-inspector-layout': 'stacked' }}
            placeholder="Choisir une variable"
            data={withChosenOption(
              variableOptions,
              variableKey,
              chosenVariable?.name || itemProps.variableName,
            )}
            value={variableKey}
            searchable
            clearable
            nothingFoundMessage={isTemplate
              ? 'Aucune variable'
              : 'Aucune variable ne contient un lien intégrable'}
            /* On copie la valeur, comme le nœud variable d'un texte : le site
               publié ne dépend d'aucune résolution à l'affichage, et
               « Rafraîchir les variables » la remet à jour. */
            onChange={(key) => {
              const variable = key ? catalog.byKey[key] : null;
              updateItemProps({
                variableKey: key || '',
                variableName: variable?.name || '',
                variableValue: variable?.value || '',
                variableOrphan: false,
              });
            }}
            comboboxProps={{ withinPortal: false }}
          />
          {variableKey && (itemProps.variableValue || isTemplate) && (
            <Text size="xs" c="dimmed">
              {itemProps.variableValue
                ? `Valeur actuelle : ${itemProps.variableValue}`
                : 'La valeur sera lue dans le document à la génération du site.'}
            </Text>
          )}
          {/* Une valeur manquante se VOIT : le bloc n'affiche rien sur le site
              publié, et une ligne grise de plus passait inaperçue. */}
          <LostTargetAlert
            shown={!!variableKey && !itemProps.variableValue && !isTemplate && !variableGone}
            what="Cette variable n’est pas encore renseignée dans ce document"
            consequence={'. Le bloc n’affichera rien tant qu’elle est vide.'}
          />
          <LostTargetAlert
            shown={itemProps.variableOrphan === true || variableGone}
            what="Cette variable n’existe plus dans le CRM"
            consequence={'. Le bloc affiche toujours la dernière adresse connue '
              + '; elle ne se mettra plus à jour.'}
          />
        </>
      )}

      {source === 'url' && urlField.render({ value, onChange })}
    </Stack>
  );
}
